import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { FALLBACK_LEAD_SOURCE_OPTIONS } from '../../data/leadSourceOptions';
import type { Lead } from '../../types';

interface LeadSourceState {
  options: string[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

const initialState: LeadSourceState = {
  options: FALLBACK_LEAD_SOURCE_OPTIONS,
  loading: false,
  error: null,
  loaded: false,
};

const normalizeAndMerge = (...sourceGroups: Array<Array<string | undefined | null>>) => {
  const merged = new Set<string>();
  sourceGroups.flat().forEach((source) => {
    const value = (source || '').trim();
    if (value) merged.add(value);
  });
  return Array.from(merged);
};

export const fetchLeadSourceOptions = createAsyncThunk(
  'leadSource/fetchLeadSourceOptions',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { leads?: { leads?: Lead[] } };
      const leadSources = (state.leads?.leads || []).map((lead) => lead.source);
      return normalizeAndMerge(FALLBACK_LEAD_SOURCE_OPTIONS, leadSources);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to build lead sources');
    }
  }
);

const leadSourceSlice = createSlice({
  name: 'leadSource',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeadSourceOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadSourceOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.options = action.payload;
      })
      .addCase(fetchLeadSourceOptions.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload as string;
      });
  },
});

export default leadSourceSlice.reducer;

export const selectLeadSourceOptions = (state: { leadSource: LeadSourceState }) =>
  state.leadSource.options;
export const selectLeadSourceLoading = (state: { leadSource: LeadSourceState }) =>
  state.leadSource.loading;
export const selectLeadSourceLoaded = (state: { leadSource: LeadSourceState }) =>
  state.leadSource.loaded;
